import {hasServerFlag, SERVER_FLAGS} from '../../../../../config';
import {type RunsLinePlotConfig} from '../../../../PanelRunsLinePlot/types';
import {
  type DerivedPointVisualizationOption,
  isPointVizFullFidelity,
  type LinePlotSettings,
} from '../../types';
import {
  DEFAULT_FULL_SCREEN_MAX_BUCKETED_RUNS,
  DEFAULT_LINE_PLOT_MAX_NUM_BUCKETED_RUNS,
  DEFAULT_LINE_PLOT_NUM_RUNS_SAMPLING,
  DEFAULT_OVERRIDE_MAX_BUCKETED_RUNS,
  DEFAULT_PANEL_MAX_NUM_RUNS,
} from '../maxRunDefaults';

/**
 * Resolve the effective max-runs value after cascading workspace -> section
 * -> panel settings. Takes the user's saved value (`limit` from panel config,
 * falling back to `maxRuns` from workspace settings) and clamps it to the
 * environment-aware ceiling from `getMaxRunsCeiling`. Returns `undefined`
 * when no setting has been saved, letting callers fall back to their own
 * defaults.
 *
 * Note: this is called via `getLinePlotSettingsWithDefaults`, which resolves
 * settings for multiple panel types (line plots, scalar charts, parallel
 * coordinates). It intentionally uses the generic panel ceiling (`isLinePlot`
 * defaults to false) so non-line-plot panels get the higher 300-run sampling
 * limit rather than the line-plot-specific 100.
 */
export const getCascadingMaxRuns = ({
  canOverrideMaxRuns,
  pointVisualizationMethod,
  maxRuns,
  limit,
  isFullScreen,
  canIncreaseFullScreenMaxRuns,
}: {
  canOverrideMaxRuns?: boolean;
  pointVisualizationMethod: DerivedPointVisualizationOption;
  maxRuns: LinePlotSettings['maxRuns'];
  limit?: RunsLinePlotConfig['limit'];
  isFullScreen?: boolean;
  canIncreaseFullScreenMaxRuns?: boolean;
}) => {
  const isBucketing = isPointVizFullFidelity(pointVisualizationMethod);

  const maxValue = getMaxRunsCeiling({
    canOverrideMaxRuns: canOverrideMaxRuns ?? false,
    isBucketing,
    isFullScreen,
    canIncreaseFullScreenMaxRuns,
  });

  const passedLimit = limit ?? maxRuns;
  if (passedLimit == null) {
    return undefined;
  }

  // if both are undefined above than we'll have a value here even if TS can't pick it up
  return Math.min(maxValue, (limit ?? maxRuns) as number);
};

/**
 * Return the hard upper bound on the number of runs a user can configure
 * for a chart. This is a *policy* function — it doesn't read or modify the
 * user's saved setting; it only determines the ceiling that other functions
 * (getCascadingMaxRuns, clampMaxRunsForLinePlot) clamp against.
 *
 * Priority order for bucketing mode:
 * 1. Server flag limit — if set, it always wins (server environments may be less performant).
 * 2. Ramp flags — each applicable flag can only raise the ceiling, never lower it.
 *    The final ceiling is the max of the base default and any active flag overrides:
 *    - `canOverrideMaxRuns` → DEFAULT_OVERRIDE_MAX_BUCKETED_RUNS
 *    - `isFullScreen` + `canIncreaseFullScreenMaxRuns` → DEFAULT_FULL_SCREEN_MAX_BUCKETED_RUNS
 * 3. Default — DEFAULT_LINE_PLOT_MAX_NUM_BUCKETED_RUNS (50).
 *
 * Non-bucketing (sampling) mode bypasses all of the above and returns either
 * DEFAULT_LINE_PLOT_NUM_RUNS_SAMPLING (line plots) or DEFAULT_PANEL_MAX_NUM_RUNS (other panels).
 *
 */
export const getMaxRunsCeiling = ({
  canOverrideMaxRuns,
  isBucketing,
  isFullScreen = false,
  canIncreaseFullScreenMaxRuns = false,
  isLinePlot = false,
}: {
  canOverrideMaxRuns: boolean;
  isBucketing: boolean;
  isFullScreen?: boolean;
  canIncreaseFullScreenMaxRuns?: boolean;
  isLinePlot?: boolean;
}) => {
  // these limits only apply to bucketing
  // sampling limits are exempt
  if (!isBucketing) {
    return isLinePlot
      ? DEFAULT_LINE_PLOT_NUM_RUNS_SAMPLING
      : DEFAULT_PANEL_MAX_NUM_RUNS;
  }
  // if it's FF fidelity it's a line plot AND the server limit applies
  const hasServerLimit = hasServerFlag(SERVER_FLAGS.maxRunsLimit);

  // this gets kind of weird - the default hardcoded value is a check against a config flag
  // ```
  // DEFAULT_LINE_PLOT_MAX_NUM_BUCKETED_RUNS = serverFlagMaxRunsLimit() // has a default if undefined
  // ```
  // this means that IF a server limit is configured for an environment, it will show up as this value
  // however since it's defaulted we have to check if the underlying config value exists before we use it
  // This way we can fall through when there's not an active server flag limit and check for the ramp flag
  // but then ultimately we'll end up returning the defaulted value
  const serverLimitOverride = hasServerLimit
    ? DEFAULT_LINE_PLOT_MAX_NUM_BUCKETED_RUNS
    : undefined;

  // if this value isn't undefined it's because there is a set server limit, and we'll use that
  if (serverLimitOverride) {
    return serverLimitOverride;
  }

  // each flag can only raise the ceiling, never lower it
  let ceiling = DEFAULT_LINE_PLOT_MAX_NUM_BUCKETED_RUNS;

  if (canOverrideMaxRuns) {
    ceiling = Math.max(ceiling, DEFAULT_OVERRIDE_MAX_BUCKETED_RUNS);
  }

  if (isFullScreen && canIncreaseFullScreenMaxRuns) {
    ceiling = Math.max(ceiling, DEFAULT_FULL_SCREEN_MAX_BUCKETED_RUNS);
  }

  return ceiling;
};

/**
 * Clamp a panel-level max-runs value to the line-plot-specific ceiling.
 * Used during settings inheritance (usePanelInheritedSettings) to ensure
 * that a panel's resolved `limit` never exceeds what the current
 * environment and feature flags allow for line plots.
 */
export const clampMaxRunsForLinePlot = (
  {
    isBucketing,
    canOverrideMaxRuns,
    isFullScreen,
    canIncreaseFullScreenMaxRuns,
  }: {
    isBucketing: boolean;
    canOverrideMaxRuns: boolean;
    isFullScreen?: boolean;
    canIncreaseFullScreenMaxRuns?: boolean;
  },
  limitFromPanelConfig?: number
) => {
  const maxRunLimit = getMaxRunsCeiling({
    canOverrideMaxRuns,
    isBucketing,
    isFullScreen,
    canIncreaseFullScreenMaxRuns,
    isLinePlot: true,
  });
  return clampToLimit(limitFromPanelConfig, maxRunLimit);
};

/** Clamp a value to an upper bound, passing through `undefined` unchanged. */
export const clampToLimit = (value: number | undefined, limit: number) => {
  if (value) {
    return Math.min(value, limit);
  }
  return value;
};
